import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import FleetTitle from '@/libs/antd/components/Title';
import {
	useSearchFinancingLimit,
	useSearchOptions,
} from '@/libs/fleet-api/hooks/simulation';
import { ArrowRightOutlined, FileSearchOutlined } from '@ant-design/icons';
import { Button, DatePicker, Input, Select, Space, Table } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';

/**
 * Type for Simulation List Table Row
 */
export interface SimulationListItem {
	key: number;
	noSpf: string;
	namaCustomer: string;
	groupBmpp: string;
	tanggalPersetujuan: string;
	limitPembiayaan: string;
	sifatLimit: string;
	jenisPlafon: string;
	expiredDate: string;
	sumberOrder: string;
}

/**
 * Simulation List Page
 */
const FRMSimulationListPage: FC = () => {
	const { navigate } = useApp();

	const {
		data: options,
		isLoading: optionsLoading,
		error: optionsError,
	} = useSearchOptions();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const searchByOptions = options?.searchByOptions || [];

	const { openCustomerDetail, openCustomerGroupDetail } = useModals();

	const [searchBy, setSearchBy] = useState<string>(
		searchByOptions[0]?.id || '',
	);
	const [searchValue, setSearchValue] = useState('');
	const [searchValueLabel, setSearchValueLabel] = useState('');
	const [page, setPage] = useState(1);
	const pageSize = 10;
	const [triggeredSearch, setTriggeredSearch] = useState<{
		by: string;
		value: string;
	} | null>(null);

	// Update searchBy default when options load
	useEffect(() => {
		if (searchByOptions.length && !searchBy) {
			setSearchBy(searchByOptions[0].id);
		}
	}, [searchByOptions, searchBy]);

	// Utility to convert kebab-case to camelCase
	const kebabToCamel = (str: string) => {
		return str.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
	};

	// Compute dropdown keys dynamically from options object
	const dropdownKeys = options
		? Object.keys(options).filter((k) => k !== 'searchByOptions')
		: [];
	const isDropdownSearch =
		dropdownKeys.includes(kebabToCamel(searchBy) + 'Options') ||
		searchBy === 'order-source';

	// Create a mapping for dropdown arrays
	const dropdownArrays: Record<
		string,
		Array<{ id: string | number; value: string }>
	> = {
		...(options?.plafonTypeOptions
			? { plafonTypeOptions: options.plafonTypeOptions }
			: {}),
		...(options?.financingLimitTypeOptions
			? { financingLimitTypeOptions: options.financingLimitTypeOptions }
			: {}),
		...(options?.orderSource ? { orderSource: options.orderSource } : {}),
	};

	// Get dropdown options based on searchBy (map camelCase back to original key)
	let dropdownOptions: { label: string; value: string | number }[] = [];
	const dropdownKey =
		searchBy === 'order-source'
			? 'orderSource'
			: kebabToCamel(searchBy) + 'Options';

	if (
		isDropdownSearch &&
		dropdownArrays[dropdownKey] &&
		Array.isArray(dropdownArrays[dropdownKey])
	) {
		const arr = dropdownArrays[dropdownKey] as Array<{
			id: string | number;
			value: string;
		}>;
		dropdownOptions = arr.map((opt) => ({ label: opt.value, value: opt.id }));
	}

	// When searchBy changes to a dropdown type, default searchValue to first option
	useEffect(() => {
		if (isDropdownSearch && dropdownOptions.length) {
			setSearchValue(dropdownOptions[0].value.toString());
			setSearchValueLabel(dropdownOptions[0].label);
		} else if (!isDropdownSearch) {
			setSearchValue('');
			setSearchValueLabel('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchBy, options]);

	// Reset page to 1 on new search
	useEffect(() => {
		if (triggeredSearch) setPage(1);
	}, [triggeredSearch]);

	// Define search keys that have specific parameters in the API hook
	const specialSearchKeys = [
		'approval-date',
		'financing-limit-type',
		'plafon-type',
		'expired-date',
		'order-source',
	];

	const { data, isLoading, error } = useSearchFinancingLimit({
		pagination: { pageSize, pageNumber: page },
		// MODIFICATION: Generic search is now only applied if the search 'by' is not a special key.
		...(triggeredSearch &&
			triggeredSearch.value &&
			!specialSearchKeys.includes(triggeredSearch.by)
			? { search: { by: triggeredSearch.by, value: triggeredSearch.value } }
			: {}),

		// MODIFICATION: Specific parameter handling for each special key.
		...(triggeredSearch?.by === 'approval-date' && triggeredSearch?.value
			? { approvalDate: triggeredSearch.value }
			: {}),
		...(triggeredSearch?.by === 'financing-limit-type' && triggeredSearch?.value
			? { financingLimitTypeId: Number(triggeredSearch.value) }
			: {}),
		...(triggeredSearch?.by === 'plafon-type' && triggeredSearch?.value
			? { plafonTypeId: Number(triggeredSearch.value) }
			: {}),
		...(triggeredSearch?.by === 'expired-date' && triggeredSearch?.value
			? { expiredDate: triggeredSearch.value }
			: {}),
		...(triggeredSearch?.by === 'order-source' && triggeredSearch?.value
			? { orderSourceId: Number(triggeredSearch.value) }
			: {}),
	});

	const tableData = (data?.items || []).map((item, index) => ({
		key: index,
		spfId: item.spfId,
		noSpf: item.spfNo,
		napResolutionId: item.napResolutionId,
		facilityId: item.facilityId,
		namaCustomer: item.customerName,
		customerId: item.customerId,
		groupBmpp: item.bmppGroupName,
		bmppGroupId: item.bmppGroupId,
		tanggalPersetujuan: item.approvalDate,
		limitPembiayaan: item.existingFinancingLimit?.toLocaleString() ?? '',
		sifatLimit: item.financingLimitType,
		jenisPlafon: item.plafonType,
		expiredDate: item.expiredDate,
		sumberOrder: item.orderSource,
	}));

	const handleViewSimulasi = (spfId: number) => {
		try {
			navigate('FRMSimulationHistoryListPage', { spfId });
		} catch (error) {
			console.error('Navigation error:', error);
		}
	};

	const handlePilihFasilitas = (spfId: number) => {
		try {
			navigate('SimulationFacilityListPage', { spfId });
		} catch (error) {
			console.error('Navigation error:', error);
		}
	};

	const handleSearch = () => {
		setTriggeredSearch({ by: searchBy, value: searchValue });
	};

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<FleetTitle>Daftar Simulasi</FleetTitle>
			<div style={{ display: 'flex', gap: 8, width: '100%' }}>
				<span
					style={{
						whiteSpace: 'nowrap',
						display: 'flex',
						alignItems: 'center',
						fontWeight: 700,
						marginRight: 100,
						marginLeft: 40,
					}}
				>
					Kata Kunci
				</span>
				<Select
					loading={optionsLoading}
					disabled={optionsLoading || !!optionsError}
					style={{ minWidth: 160 }}
					value={searchBy}
					onChange={(value) => {
						setSearchBy(value);
						// Reset search value when changing type to avoid mismatched data
						setSearchValue('');
						setSearchValueLabel('');
					}}
					options={searchByOptions.map((opt) => ({
						label: opt.value,
						value: opt.id,
					}))}
					placeholder="Cari berdasarkan"
				/>
				{searchBy === 'approval-date' || searchBy === 'expired-date' ? (
					<DatePicker
						value={searchValue ? dayjs(searchValue) : null}
						onChange={(date: dayjs.Dayjs | null) => {
							const valueToSave = date ? date.toISOString() : '';
							setSearchValue(valueToSave);
						}}
						style={{ borderRadius: 8, width: '100%' }}
						format="DD-MM-YYYY"
					/>
				) : isDropdownSearch ? (
					<Select
						style={{ minWidth: 160, width: '100%' }}
						value={searchValue ? searchValueLabel : undefined}
						onClear={() => {
							setSearchValue('');
							setSearchValueLabel('');
						}}
						onChange={(_, option) => {
							setSearchValue(
								option
									? String((option as { label: string; value: number }).value)
									: '',
							);
							setSearchValueLabel(
								option
									? String((option as { label: string; value: number }).label)
									: '',
							);
						}}
						allowClear
						options={dropdownOptions}
						placeholder="Pilih opsi..."
					/>
				) : (
					<Input
						placeholder="Masukkan kata kunci..."
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						onPressEnter={handleSearch}
						style={{ borderRadius: 8 }}
						allowClear
					/>
				)}
				<Button
					type="primary"
					onClick={handleSearch}
					style={{ width: 300, fontWeight: 700 }}
					loading={isLoading || optionsLoading}
				// disabled={!searchBy || !searchValue}
				>
					SEARCH
				</Button>
			</div>
			{optionsError && (
				<div style={{ color: 'red' }}>Gagal memuat opsi pencarian.</div>
			)}
			<FleetTitle>Informasi Limit Pembiayaan</FleetTitle>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={tableData}
				columns={[
					{
						title: 'No',
						width: 50,
						align: 'center',
						render: (_, __, index) => (page - 1) * pageSize + index + 1,
					},
					{
						title: 'No.SPF',
						dataIndex: 'noSpf',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									// TODO: change to actual prospect id
									navigate('SPFPage', {
										spfId: record.spfId,
										napResolutionId: record.napResolutionId,
										onBack: () => navigate('FRMSimulationListPage'),
										viewOnly: true,
									});
								}}
							>
								{record.noSpf}
							</a>
						),
					},
					{
						title: 'Nama Customer',
						dataIndex: 'namaCustomer',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(record.customerId);
								}}
							>
								{record.namaCustomer}
							</a>
						),
					},
					{
						title: 'Group BMPP',
						dataIndex: 'groupBmpp',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									openCustomerGroupDetail(record.bmppGroupId);
								}}
							>
								{record.groupBmpp}
							</a>
						),
					},
					{
						title: 'Tanggal Persetujuan',
						dataIndex: 'tanggalPersetujuan',
						render: (date) => (date ? dayjs(date).format('DD-MM-YYYY') : '-'),
					},
					{
						title: 'Limit Pembiayaan Existing',
						dataIndex: 'limitPembiayaan',
						align: 'right',
					},
					{
						title: 'Sifat Limit',
						dataIndex: 'sifatLimit',
					},
					{
						title: 'Jenis Plafond',
						dataIndex: 'jenisPlafon',
					},
					{
						title: 'Expired Date',
						dataIndex: 'expiredDate',
						render: (date) => (date ? dayjs(date).format('DD-MM-YYYY') : '-'),
					},
					{
						title: 'Sumber Order',
						dataIndex: 'sumberOrder',
					},
					{
						title: 'View Simulasi',
						width: 120,
						align: 'center',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									handleViewSimulasi(record.spfId);
								}}
								style={{ color: '#1557BC' }}
								aria-label="View Simulasi"
							>
								<FileSearchOutlined />
							</a>
						),
					},
					{
						title: 'Pilih Fasilitas',
						width: 120,
						align: 'center',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									handlePilihFasilitas(record.spfId);
								}}
								style={{ color: '#1557BC' }}
								aria-label="Pilih Fasilitas"
							>
								<ArrowRightOutlined />
							</a>
						),
					},
				]}
				pagination={{
					current: page,
					total: data?.totalCount || 0,
					pageSize,
					onChange: setPage,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} of ${total} items`,
				}}
				rowKey="key"
			/>
			{error && <div style={{ color: 'red' }}>Gagal memuat data simulasi.</div>}
		</Space>
	);
};

export default FRMSimulationListPage;
